import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { transcriptionApi } from '../lib/transcription-api.js';
import { queryKeys } from '../lib/query-keys.js';
import type { CreateTranscriptionInput } from '../types/transcription.js';

/**
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business 文字起こし一覧を取得するhook
 * @graph-connects transcriptionApi.list [calls] 一覧API
 */
export function useTranscriptionList() {
  return useQuery({
    queryKey: queryKeys.transcription.list(),
    queryFn: () => transcriptionApi.list(),
  });
}

/**
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business 文字起こし詳細を取得するhook。processing中は3秒ポーリングする。
 * @graph-connects transcriptionApi.get [calls] 詳細API
 */
export function useTranscriptionDetail(id: string) {
  return useQuery({
    queryKey: queryKeys.transcription.detail(id),
    queryFn: () => transcriptionApi.get(id),
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      return status === 'pending' || status === 'processing' ? 3000 : false;
    },
  });
}

/**
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business 文字起こしを新規作成するmutation hook
 * @graph-connects transcriptionApi.create [calls] 作成API
 */
export function useCreateTranscription() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateTranscriptionInput) => transcriptionApi.create(input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.transcription.all });
    },
  });
}

/**
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business 文字起こしを削除するmutation hook
 * @graph-connects transcriptionApi.remove [calls] 削除API
 */
export function useDeleteTranscription() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => transcriptionApi.remove(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.transcription.all });
    },
  });
}

/**
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business 文字起こしをアーカイブするmutation hook
 * @graph-connects transcriptionApi.archive [calls] アーカイブAPI
 */
export function useArchiveTranscription() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => transcriptionApi.archive(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.transcription.all });
    },
  });
}
